import { type ReactElement, Suspense, useEffect, useMemo } from "react";
import { retryLazy } from "@claroty/utilities/asyncUtils";
import { Slide, ToastContainer } from "react-toastify";
import { initBrandCssSkin } from "~/utils/infra/productVerticalUtils";
import { isNullOrUndefined } from "../utils/generalUtils";
import ConnectedConfirmationDialog from "./common/ConfirmationDialog/ConnectedConfirmationDialog";
import ConnectedDialog from "./common/Dialog/ConnectedDialog";
import ErrorBoundary from "./common/ErrorBoundary/ErrorBoundary";
import ConnectedLoadingContainer from "./common/LoadingContainer/ConnectedLoadingContainer";
import PreAuthApp from "./PreAuthApp";
import AppAnalyticsHelper from "./app/AppAnalyticsHelper";
import NewAppVersion from "./app/NewAppVersion/NewAppVersion";
import { AUTO_CLOSE_SECONDS } from "./app/Notifications/notifications";

// Lazy components
const App = retryLazy(() => import("./App"));

type BaseAppProps = {
    isAuthenticated: boolean | undefined;
    onLogin: () => void;
};

const BaseApp = ({ isAuthenticated, onLogin }: BaseAppProps) => {
    useEffect(() => {
        initBrandCssSkin();
    }, []);

    const app = useMemo<ReactElement | null>(() => {
        const isLoadingAuthentication = isNullOrUndefined(isAuthenticated);
        if (isLoadingAuthentication) return null;

        if (isAuthenticated)
            return (
                <Suspense fallback={null}>
                    <App />
                </Suspense>
            );

        return <PreAuthApp onLogin={onLogin} />;
    }, [isAuthenticated, onLogin]);

    return (
        <ErrorBoundary section="app">
            <AppAnalyticsHelper />
            <ConnectedLoadingContainer />
            <ConnectedConfirmationDialog />
            <ConnectedDialog />
            <NewAppVersion />
            <ToastContainer
                position="bottom-right"
                autoClose={AUTO_CLOSE_SECONDS * 1000}
                newestOnTop={false}
                rtl={false}
                draggable={true}
                pauseOnHover={true}
                transition={Slide}
            />
            {app}
        </ErrorBoundary>
    );
};

export default BaseApp;
